import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Voegencalculator: rekent uit hoeveel zakken voegmiddel nodig zijn en zet dat
 * aantal in het (geneste) product-form-component, dat het toevoegen en het
 * verversen van de cart drawer afhandelt.
 *
 * Verbruiksformule uit het datablad van Schönox SF Design (nov 2024):
 *   g/m² ≈ A × B × (C + D) / (C × D) × 1800 × 0,83
 *   A = voegbreedte (mm), B = voegdiepte (cm), C/D = tegelmaten (cm)
 * Hier met de tegelmaten omgerekend naar mm en de voegdiepte in mm, zodat:
 *   kg/m² = A × B × (C + D) / (C × D) × dichtheid × formulefactor
 * met A, B, C, D allemaal in mm en de dichtheid in kg/l. Dichtheid en
 * formulefactor komen uit metafields op het voegmiddel.
 *
 * @typedef {Object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLInputElement} areaInput
 * @property {HTMLInputElement} lengthInput
 * @property {HTMLInputElement} widthInput
 * @property {HTMLInputElement} jointWidthInput
 * @property {HTMLInputElement} jointDepthInput
 * @property {HTMLElement} error
 * @property {HTMLElement} depthHint
 * @property {HTMLElement} result
 * @property {HTMLElement} bagsValue
 * @property {HTMLElement} bagsLabel
 * @property {HTMLElement} bagSize
 * @property {HTMLElement} theoreticalKg
 * @property {HTMLElement} adviceKg
 *
 * @extends {Component<Refs>}
 */
class VoegencalculatorComponent extends Component {
  requiredRefs = ['dialog', 'areaInput', 'lengthInput', 'widthInput', 'jointWidthInput', 'jointDepthInput', 'result', 'error'];

  connectedCallback() {
    super.connectedCallback();
    if (this.refs.bagSize) this.refs.bagSize.textContent = this.#format(this.#bagSize, 0, 2);
    this.#update(false);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.closest('.shopify-section, dialog')?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.closest('.shopify-section, dialog')?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * Klapt de uitleg bij voegdiepte open of dicht.
   * @param {MouseEvent & {target: HTMLElement}} event
   */
  toggleHint(event) {
    const hint = this.refs.depthHint;
    if (hint) this.#setHint(hint.hidden);
  }

  /** @param {boolean} open */
  #setHint(open) {
    this.refs.depthHint.hidden = !open;
    this.querySelector('.voegencalculator__info')?.setAttribute('aria-expanded', String(open));
    if (!open) return;
    // De pijl wijst naar het icoon, ongeacht hoe breed het veld is.
    const info = this.querySelector('.voegencalculator__info');
    const hint = this.refs.depthHint;
    if (info) {
      const arrowRight = hint.getBoundingClientRect().right - (info.getBoundingClientRect().left + info.offsetWidth / 2) - 6;
      hint.style.setProperty('--voegencalculator-arrow-right', `${Math.max(8, arrowRight)}px`);
    }
  }

  /** Opent de popup. */
  open() {
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
  }

  /** Sluit de popup. */
  close() {
    this.refs.dialog.close();
  }

  /**
   * Sluit de popup bij een klik op de achtergrond (buiten de inhoud).
   * NB: component.js geeft hier een proxy-event door waarvan `target` altijd de
   * dialog zelf is, ook bij een klik op een veld erbinnen. Het echte doel staat
   * daarom in composedPath().
   * @param {MouseEvent} event
   */
  handleDialogClick(event) {
    // Een klik ergens anders in de popup sluit de uitleg bij voegdiepte.
    const target = event.composedPath()[0];
    if (this.refs.depthHint && !this.refs.depthHint.hidden && !(target instanceof Element && target.closest('.voegencalculator__info'))) {
      this.#setHint(false);
    }
    if (target === this.refs.dialog) this.close();
  }

  /**
   * Sluit de popup nadat dit voegmiddel is toegevoegd, zodat de cart drawer zichtbaar is.
   * @param {Event} event
   */
  /**
   * Houdt de beschikbaarheid gelijk met de gekozen variant, zodat de knop na een
   * variantwissel niet onterecht (de)activeert. Zelfde productcontrole als product-form.js.
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    const productId = this.querySelector('product-form-component')?.getAttribute('data-product-id');
    const eventProductId = detail?.data?.newProduct?.id ?? detail?.data?.productId;
    if (String(eventProductId) !== productId) return;
    this.dataset.available = String(detail.resource?.available === true);
    // product-form-component luistert op de dialog en mist dit event, dus het variant-id
    // in ons formulier moeten we zelf bijwerken, anders komt de oude variant in de winkelwagen.
    const variantId = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (variantId && detail.resource?.id) variantId.value = String(detail.resource.id);
    this.#update(false);
  };

  #onCartUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    const productId = this.querySelector('product-form-component')?.getAttribute('data-product-id');
    if (detail?.data?.source === 'product-form-component' && String(detail.data.productId) === productId && !detail.data.didError) {
      if (this.refs.dialog.open) this.close();
    }
  };

  get #bagSize() {
    return Number(this.dataset.bagSize) || 0;
  }

  /** Handler voor typen: rekent live mee, maar toont nog geen foutmeldingen. */
  handleInput() {
    this.#syncSizeButtons();
    this.#update(false);
  }

  /** Handler voor het verlaten van een veld: nu wel foutmeldingen tonen. */
  handleChange() {
    this.#update(true);
  }

  /**
   * Snelkeuze voor een tegelformaat.
   * @param {MouseEvent & {target: HTMLElement}} event
   */
  selectSize(event) {
    const button = /** @type {HTMLElement | null} */ (event.target.closest('[data-length]'));
    if (!button) return;
    this.refs.lengthInput.value = button.dataset.length ?? '';
    this.refs.widthInput.value = button.dataset.width ?? '';
    this.#syncSizeButtons();
    this.#update(true);
    if (!this.refs.jointWidthInput.value) this.refs.jointWidthInput.focus();
  }

  #syncSizeButtons() {
    const length = this.#parse(this.refs.lengthInput.value);
    const width = this.#parse(this.refs.widthInput.value);
    this.querySelectorAll('.voegencalculator__size').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      const pressed =
        (Number(button.dataset.length) === length && Number(button.dataset.width) === width) ||
        (Number(button.dataset.length) === width && Number(button.dataset.width) === length);
      button.setAttribute('aria-pressed', String(pressed));
    });
  }

  /**
   * Leest een getal; accepteert zowel een komma als een punt als decimaalteken.
   * @param {string} value
   * @returns {number}
   */
  #parse(value) {
    const normalized = value.trim().replace(/\s/g, '').replace(',', '.');
    if (normalized === '' || !/^\d*\.?\d+$|^\d+\.$/.test(normalized)) return NaN;
    return Number.parseFloat(normalized);
  }

  /**
   * @param {number} value
   * @param {number} [min]
   * @param {number} [max]
   */
  #format(value, min = 1, max = 1) {
    return new Intl.NumberFormat('nl-NL', { minimumFractionDigits: min, maximumFractionDigits: max }).format(value);
  }

  /**
   * Valideert één veld. Geeft een foutmelding terug, of null als het veld leeg of geldig is.
   * @param {HTMLInputElement} input
   * @returns {{ value: number, message: string | null }}
   */
  #readField(input) {
    const value = this.#parse(input.value);
    const label = input.dataset.label ?? '';
    const min = Number(input.dataset.min);
    const max = Number(input.dataset.max);
    const unit = input.parentElement?.querySelector('.voegencalculator__unit')?.textContent ?? '';

    if (input.value.trim() === '') return { value: NaN, message: null };
    if (!Number.isFinite(value)) return { value, message: `Vul bij ${label} een getal in.` };
    if (value < min) return { value, message: `De ${label} moet minimaal ${this.#format(min, 0, 2)} ${unit} zijn.` };
    if (value > max) return { value, message: `De ${label} mag maximaal ${this.#format(max, 0, 2)} ${unit} zijn.` };
    return { value, message: null };
  }

  /**
   * Koppelt de zichtbare foutmelding via aria-describedby aan het veld waar hij over gaat,
   * en haalt de koppeling bij andere velden weg. Bestaande verwijzingen (zoals de hint bij
   * voegdiepte) blijven behouden.
   * @param {HTMLInputElement | null} target
   */
  #linkError(target) {
    const errorId = this.refs.error.id;
    for (const input of [
      this.refs.areaInput,
      this.refs.lengthInput,
      this.refs.widthInput,
      this.refs.jointWidthInput,
      this.refs.jointDepthInput,
    ]) {
      const ids = (input.getAttribute('aria-describedby') ?? '').split(/\s+/).filter((id) => id && id !== errorId);
      if (input === target) ids.push(errorId);
      if (ids.length) input.setAttribute('aria-describedby', ids.join(' '));
      else input.removeAttribute('aria-describedby');
    }
  }

  /**
   * @param {boolean} showErrors - Foutmeldingen tonen (na het verlaten van een veld).
   */
  #update(showErrors) {
    const { areaInput, lengthInput, widthInput, jointWidthInput, jointDepthInput, error } = this.refs;
    const inputs = [areaInput, lengthInput, widthInput, jointWidthInput, jointDepthInput];

    /** @type {string | null} */
    let firstMessage = null;
    /** @type {HTMLInputElement | null} */
    let firstInvalidInput = null;
    const values = inputs.map((input) => {
      const field = this.#readField(input);
      const invalid = field.message !== null;
      if (invalid && !firstMessage) {
        firstMessage = field.message;
        firstInvalidInput = input;
      }
      // Rood maken mag direct bij typen als het veld al eerder als fout gemarkeerd was.
      if (showErrors || !invalid) input.setAttribute('aria-invalid', String(invalid));
      return field.message === null ? field.value : NaN;
    });

    // Een nog niet getoonde fout verschijnt pas na het verlaten van een veld, maar een
    // al zichtbare fout wordt tijdens het typen wel bijgewerkt of weggehaald.
    if (showErrors || !error.hidden || !firstMessage) {
      error.textContent = firstMessage ?? '';
      error.hidden = !firstMessage;
      this.#linkError(firstMessage ? firstInvalidInput : null);
    }

    const [area, lengthCm, widthCm, jointWidth, jointDepth] = values;
    if (!values.every((value) => Number.isFinite(value) && value > 0)) {
      this.#showResult(null);
      return;
    }

    const length = lengthCm * 10;
    const width = widthCm * 10;
    const density = Number(this.dataset.density);
    const formulaFactor = Number(this.dataset.formulaFactor) || 1;
    const margin = Number(this.dataset.margin) || 0;

    const theoreticalKg = area * jointWidth * jointDepth * ((length + width) / (length * width)) * density * formulaFactor;
    const adviceKg = theoreticalKg * (1 + margin / 100);
    // Kleine marge tegen afrondingsruis, zodat bv. precies 10,0 kg bij zakken van 5 kg geen 3 zakken wordt.
    const bags = Math.max(1, Math.ceil(adviceKg / this.#bagSize - 1e-9));

    this.#showResult({ theoreticalKg, adviceKg, bags });
  }

  /**
   * @param {{ theoreticalKg: number, adviceKg: number, bags: number } | null} calculation
   */
  #showResult(calculation) {
    const { result } = this.refs;
    const available = this.dataset.available === 'true';
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-voegencalculator-quantity]')
    );
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="addToCartButton"]'));
    const buttonLabel = this.querySelector('[data-voegencalculator-button-label]');

    if (!calculation) {
      result.hidden = true;
      if (button) button.disabled = true;
      if (buttonLabel && available) buttonLabel.textContent = 'Vul eerst je maten in';
      return;
    }

    const { theoreticalKg, adviceKg, bags } = calculation;
    const unit = bags === 1 ? 'zak' : 'zakken';

    this.refs.bagsValue.textContent = String(bags);
    this.refs.bagsLabel.textContent = unit;
    this.refs.theoreticalKg.textContent = this.#format(theoreticalKg);
    this.refs.adviceKg.textContent = this.#format(adviceKg);
    const wasHidden = result.hidden;
    result.hidden = false;
    // In een lage popup valt het resultaat anders buiten beeld zodra het verschijnt.
    if (wasHidden) result.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

    if (quantityInput) quantityInput.value = String(bags);
    if (button) button.disabled = !available;
    if (buttonLabel && available) buttonLabel.textContent = `Voeg ${bags} ${unit} toe aan winkelwagen`;
  }
}

if (!customElements.get('voegencalculator-component')) {
  customElements.define('voegencalculator-component', VoegencalculatorComponent);
}
